import QRCode from 'qrcode';
import { WEB_BASE_URL } from '../../config';

/**
 * What a printed code opens: the existing report page (pages/QRReport). The
 * authority segment is matched there by a normalised name (spaces, hyphens and
 * underscores are interchangeable), so a hyphenated slug keeps the printed URL
 * short and readable without breaking the match.
 *
 * Built from WEB_BASE_URL at render time rather than stored, so a code made in
 * a dev build never leaks a localhost URL into the DB.
 *
 * `qrCodeId` (the saved code's QrCodes.id) goes in as `?qr=`: the report page
 * records a scan against it and credits the report to it. Leave it out only
 * for a pre-creation preview, which must never be printed.
 */
export function qrReportUrl(orgKey: string, authorityName?: string | null, qrCodeId?: number): string {
	let url = `${WEB_BASE_URL}/qr-report/org/${encodeURIComponent(orgKey)}`;
	if (authorityName) url += `/${encodeURIComponent(authorityName.trim().replace(/\s+/g, '-'))}`;
	return qrCodeId ? `${url}?qr=${qrCodeId}` : url;
}

/**
 * PNG data URL of the code. Error correction 'Q' (25%) rather than the default
 * 'M': these get printed, laminated and stuck on walls, and survive a scuff or
 * a bit of glare much better.
 */
export function qrDataUrl(text: string, width = 512): Promise<string> {
	return QRCode.toDataURL(text, { errorCorrectionLevel: 'Q', margin: 2, width });
}

/** Safe filename for a download, e.g. "iitd-main-gate-poster.png". */
export function qrFileName(orgKey: string, label: string): string {
	const slug = `${orgKey}-${label}`
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	return `${slug || 'qr-code'}.png`;
}

export function downloadDataUrl(dataUrl: string, fileName: string) {
	const a = document.createElement('a');
	a.href = dataUrl;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
}

/**
 * Native share sheet with the PNG attached where the browser supports file
 * sharing (Edge/Chrome on Windows, most mobile browsers); otherwise the link
 * goes to the clipboard. Resolves to what actually happened so the caller can
 * say so.
 */
export async function shareQr(
	dataUrl: string,
	fileName: string,
	url: string,
	title: string
): Promise<'shared' | 'copied' | 'cancelled'> {
	try {
		const blob = await (await fetch(dataUrl)).blob();
		const file = new File([blob], fileName, { type: 'image/png' });
		if (navigator.canShare?.({ files: [file] })) {
			await navigator.share({ files: [file], title, text: `${title}\n${url}` });
			return 'shared';
		}
	} catch (err: any) {
		if (err?.name === 'AbortError') return 'cancelled';
		/* fall through to clipboard */
	}
	await navigator.clipboard?.writeText(url);
	return 'copied';
}
